import {
  accessSessionTokenKeyName,
  loggedInAdminAccessToken,
  loggedInAdminRefreshToken,
  refreshSessionTokenKeyName,
} from 'src/utils/constants'
import {COOKIE_CONSENT_KEY} from 'src/utils/cookieConsent'
import {
  captureInitialSyncCompletionStorage,
  restoreInitialSyncCompletionStorage,
} from 'src/utils/initialSyncCompletionStorage'
import type {AppDispatch} from 'src/redux/store'
import {logoutIfImpersonationAdminExpired} from 'src/utils/sessionExpiredLogout'

const getStoreDispatch = (): AppDispatch => {
  // Lazy require avoids store ↔ AxiosClient circular init during reducer registration.
  const {store} = require('src/redux/store') as typeof import('src/redux/store')
  return store.dispatch
}

export const ADMIN_SESSION_EXPIRED_EVENT = 'admin-session-expired'
export const IMPERSONATION_SESSION_CHANGED_EVENT = 'impersonation-session-changed'

export function notifyImpersonationSessionChanged(): void {
  window.dispatchEvent(new CustomEvent(IMPERSONATION_SESSION_CHANGED_EVENT))
}

export type ImpersonationCheckResult =
  | { ok: true }
  | { ok: false; reason: string }

export type JwtExpiryInfo = {
  exp: number
  iat: number
  expiresAt: Date
  remainingMs: number
}

export function decodeJwtExpiry(token: string): JwtExpiryInfo | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (typeof payload?.exp !== 'number') return null
    const exp = payload.exp
    const iat = typeof payload.iat === 'number' ? payload.iat : exp
    return {
      exp,
      iat,
      expiresAt: new Date(exp * 1000),
      remainingMs: Math.max(0, exp * 1000 - Date.now()),
    }
  } catch {
    return null
  }
}

export function isTokenExpired(token: string): boolean {
  const info = decodeJwtExpiry(token)
  if (!info) return true
  return info.remainingMs <= 0
}

export function getTokenRemainingMs(token: string): number | null {
  return decodeJwtExpiry(token)?.remainingMs ?? null
}

export function isImpersonating(): boolean {
  return Boolean(localStorage.getItem(loggedInAdminAccessToken))
}

export function hasValidAdminSnapshot(): boolean {
  const token = localStorage.getItem(loggedInAdminAccessToken)
  if (!token) return false
  return !isTokenExpired(token)
}

export function getAdminSessionRemainingMs(): number | null {
  const token = localStorage.getItem(loggedInAdminAccessToken)
  if (!token) return null
  return getTokenRemainingMs(token)
}

export function notifyAdminSessionExpired(): void {
  window.dispatchEvent(new CustomEvent(ADMIN_SESSION_EXPIRED_EVENT))
}

export function clearAdminSnapshot(): void {
  localStorage.removeItem(loggedInAdminAccessToken)
  localStorage.removeItem(loggedInAdminRefreshToken)
}

/** Clears admin snapshot, customer session tokens, and all local storage (cookie consent preserved). */
export function clearAllSessionsAndPrepareLogin(): void {
  const cookieConsent = localStorage.getItem(COOKIE_CONSENT_KEY)
  const initialSyncCompletions = captureInitialSyncCompletionStorage()
  clearImpersonationSessionMarkers()
  localStorage.clear()
  if (cookieConsent) localStorage.setItem(COOKIE_CONSENT_KEY, cookieConsent)
  restoreInitialSyncCompletionStorage(initialSyncCompletions)
}

export function getValidAdminAccessToken(): string | null {
  const adminToken = localStorage.getItem(loggedInAdminAccessToken)
  if (!adminToken) return null

  if (isTokenExpired(adminToken)) {
    clearAdminSnapshot()
    notifyAdminSessionExpired()
    logoutIfImpersonationAdminExpired(getStoreDispatch())
    return null
  }

  return adminToken
}

export function canStartImpersonation(
  adminAccess: string | null,
  adminRefresh: string | null
): ImpersonationCheckResult {
  if (isImpersonationSessionActive()) {
    return {
      ok: false,
      reason: 'Please return to admin before impersonating another customer.',
    }
  }

  if (!adminAccess || !adminRefresh) {
    return { ok: false, reason: 'Unauthorized access. Please sign in as admin.' }
  }

  if (isTokenExpired(adminAccess)) {
    return {
      ok: false,
      reason:
        'Your admin session has expired. Please sign in again before impersonating a customer.',
    }
  }

  return { ok: true }
}

export function startImpersonation(params: {
  customerAccess: string
  customerRefresh: string
  adminAccess: string
  adminRefresh: string
}): void {
  localStorage.setItem(loggedInAdminAccessToken, params.adminAccess)
  localStorage.setItem(loggedInAdminRefreshToken, params.adminRefresh)
  localStorage.setItem(accessSessionTokenKeyName, params.customerAccess)
  localStorage.setItem(refreshSessionTokenKeyName, params.customerRefresh)
  markImpersonationSessionActive(params.customerAccess, params.customerRefresh)
}

export function switchCustomer(params: {
  customerAccess: string
  customerRefresh: string
}): void {
  localStorage.setItem(accessSessionTokenKeyName, params.customerAccess)
  localStorage.setItem(refreshSessionTokenKeyName, params.customerRefresh)
  if (isImpersonationSessionActive()) {
    backupImpersonatedCustomerTokens(params.customerAccess, params.customerRefresh)
  }
}

export function endImpersonation(): { adminAccess: string; adminRefresh: string } | null {
  const adminAccess = localStorage.getItem(loggedInAdminAccessToken)
  const adminRefresh = localStorage.getItem(loggedInAdminRefreshToken)

  if (!adminAccess || !adminRefresh || isTokenExpired(adminAccess)) {
    clearImpersonationSessionMarkers()
    clearAdminSnapshot()
    return null
  }

  clearImpersonationSessionMarkers()
  localStorage.setItem(accessSessionTokenKeyName, adminAccess)
  localStorage.setItem(refreshSessionTokenKeyName, adminRefresh)
  clearAdminSnapshot()
  notifyImpersonationSessionChanged()

  return { adminAccess, adminRefresh }
}

export function refreshAdminSnapshot(access: string, refresh: string): void {
  localStorage.setItem(loggedInAdminAccessToken, access)
  localStorage.setItem(loggedInAdminRefreshToken, refresh)
}

/** Update active session tokens without touching the admin impersonation snapshot. */
export function setActiveSessionTokens(access: string, refresh: string): void {
  localStorage.setItem(accessSessionTokenKeyName, access)
  localStorage.setItem(refreshSessionTokenKeyName, refresh)
}

export function setActiveSessionTokensPreservingImpersonation(
  access: string,
  refresh: string
): void {
  setActiveSessionTokens(access, refresh)
  if (isImpersonationSessionActive()) {
    backupImpersonatedCustomerTokens(access, refresh)
  }
}

export const IMPERSONATION_RETURN_PATH_KEY = 'impersonationReturnPath'
export const IMPERSONATION_SESSION_FLAG = 'impersonationSessionActive'
export const IMPERSONATION_CUSTOMER_ACCESS_KEY = 'impersonationCustomerAccessBackup'
export const IMPERSONATION_CUSTOMER_REFRESH_KEY = 'impersonationCustomerRefreshBackup'

function backupImpersonatedCustomerTokens(access: string, refresh: string): void {
  sessionStorage.setItem(IMPERSONATION_SESSION_FLAG, '1')
  sessionStorage.setItem(IMPERSONATION_CUSTOMER_ACCESS_KEY, access)
  sessionStorage.setItem(IMPERSONATION_CUSTOMER_REFRESH_KEY, refresh)
  notifyImpersonationSessionChanged()
}

/** Keep sessionStorage customer backup aligned with the active bearer (e.g. before OAuth _self). */
export function syncImpersonationCustomerTokenBackup(): void {
  if (!isImpersonationSessionActive()) return

  const access = localStorage.getItem(accessSessionTokenKeyName)
  const refresh = localStorage.getItem(refreshSessionTokenKeyName)
  const adminAccess = localStorage.getItem(loggedInAdminAccessToken)

  if (!access || !refresh || (adminAccess && access === adminAccess)) return

  backupImpersonatedCustomerTokens(access, refresh)
}

export function markImpersonationSessionActive(customerAccess: string, customerRefresh: string): void {
  backupImpersonatedCustomerTokens(customerAccess, customerRefresh)
}

export function clearImpersonationSessionMarkers(): void {
  sessionStorage.removeItem(IMPERSONATION_SESSION_FLAG)
  sessionStorage.removeItem(IMPERSONATION_CUSTOMER_ACCESS_KEY)
  sessionStorage.removeItem(IMPERSONATION_CUSTOMER_REFRESH_KEY)
  sessionStorage.removeItem(IMPERSONATION_RETURN_PATH_KEY)
  notifyImpersonationSessionChanged()
}

/** Call before any fresh sign-in so prior impersonation state cannot leak into the new session. */
export function resetImpersonationForFreshLogin(): void {
  clearAdminSnapshot()
  clearImpersonationSessionMarkers()
}

/** True while admin is viewing the app as a customer (survives bfcache / token drift). */
export function isImpersonationSessionActive(): boolean {
  if (!hasValidAdminSnapshot()) {
    return false
  }

  if (isImpersonating()) return true

  const flag = sessionStorage.getItem(IMPERSONATION_SESSION_FLAG) === '1'
  if (!flag) return false

  const activeAccess = localStorage.getItem(accessSessionTokenKeyName)
  const backupAccess = sessionStorage.getItem(IMPERSONATION_CUSTOMER_ACCESS_KEY)
  const backupRefresh = sessionStorage.getItem(IMPERSONATION_CUSTOMER_REFRESH_KEY)

  if (!activeAccess || !backupAccess) {
    clearImpersonationSessionMarkers()
    return false
  }

  if (activeAccess === backupAccess) {
    return true
  }

  const activeRefresh = localStorage.getItem(refreshSessionTokenKeyName)
  if (isImpersonating() && backupRefresh && activeRefresh && activeRefresh !== backupRefresh) {
    backupImpersonatedCustomerTokens(activeAccess, activeRefresh)
    return true
  }

  clearImpersonationSessionMarkers()
  return false
}

export function peekImpersonationReturnPath(): string | null {
  return sessionStorage.getItem(IMPERSONATION_RETURN_PATH_KEY)
}

/** Restore customer bearer if browser back swapped it for the frozen admin token. */
export function restoreCustomerSessionTokensIfNeeded(): boolean {
  if (!isImpersonationSessionActive()) return false

  const activeAccess = localStorage.getItem(accessSessionTokenKeyName)
  const adminAccess = localStorage.getItem(loggedInAdminAccessToken)
  const backupAccess = sessionStorage.getItem(IMPERSONATION_CUSTOMER_ACCESS_KEY)
  const backupRefresh = sessionStorage.getItem(IMPERSONATION_CUSTOMER_REFRESH_KEY)

  const tokenDriftedToAdmin = Boolean(
    adminAccess && activeAccess && activeAccess === adminAccess
  )

  if (!tokenDriftedToAdmin) {
    if (
      isImpersonating() &&
      activeAccess &&
      backupAccess &&
      activeAccess !== backupAccess &&
      activeAccess !== adminAccess
    ) {
      backupImpersonatedCustomerTokens(
        activeAccess,
        localStorage.getItem(refreshSessionTokenKeyName) ?? backupRefresh ?? ''
      )
    }
    return false
  }

  if (!backupAccess || !backupRefresh) return false

  setActiveSessionTokens(backupAccess, backupRefresh)
  notifyImpersonationSessionChanged()
  return true
}

export function isAdminProfileDuringImpersonation(
  user: { account_type?: string; user_type?: string; parent_user_role?: unknown } | null
): boolean {
  if (!isImpersonationSessionActive() || !user) return false
  if (user.parent_user_role) return false
  return (
    user.account_type === 'SUPERADMIN' ||
    user.account_type === 'ADMIN_STAFF' ||
    user.user_type === 'SUPERADMIN'
  )
}

export function rememberImpersonationReturnPath(path?: string): void {
  if (!isImpersonationSessionActive()) return
  syncImpersonationCustomerTokenBackup()
  const returnPath = path ?? `${window.location.pathname}${window.location.search}`
  sessionStorage.setItem(IMPERSONATION_RETURN_PATH_KEY, returnPath)
}

export function consumeImpersonationReturnPath(): string | null {
  const path = sessionStorage.getItem(IMPERSONATION_RETURN_PATH_KEY)
  if (path) {
    sessionStorage.removeItem(IMPERSONATION_RETURN_PATH_KEY)
  }
  return path
}

/** Same-tab OAuth by default; Salla opens in a new tab to avoid replacing onboarding. */
export function resolveOAuthWindowTarget(platformCode?: string | null): '_blank' | '_self' {
  if ((platformCode ?? '').toUpperCase() === 'SALLA') return '_blank'
  return '_self'
}
